import React, { useEffect, useCallback, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";

import {
  getComments,
  changeCommentDataState,
  resetCommentFormState,
  deleteCommentAndUpdateGrid
} from "../../../../actions/comments";
import CommandCell from "../../../../components/GridCell/CommandCell";
import Dialog from "../../../../components/Dialog";
import columns from "./GridFields";
import styles from "../../../../assets/jss/pages/listStyle";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import {
  getCommentsList,
  getCommentsListDataState,
  getCommentsListTotalNumber
} from "../../../../reducers/comments";

const useStyles = makeStyles(styles);

const CommentsListContainer = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const idToDelete = useRef();

  const comments = useSelector(getCommentsList);
  const dataState = useSelector(getCommentsListDataState);
  const total = useSelector(getCommentsListTotalNumber);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getComments({ dataState: dataState }));
  }, [dispatch, dataState]);

  useScrollToTop({ dataState });

  const handleDataStateChange = useCallback(
    event => dispatch(changeCommentDataState({ dataState: event.dataState })),
    [dispatch]
  );

  const handleInsertClick = () => {
    dispatch(resetCommentFormState());
    history.push(`${location.pathname}/form`);
  };

  const handleEditIconClick = dataItem => {
    dispatch(resetCommentFormState());
    history.push(`${location.pathname}/form/${dataItem.id}`);
  };

  const handleDeleteIconClick = dataItem => {
    setIsDialogVisible(true);

    idToDelete.current = dataItem.id;
  };

  const closeDialog = () => {
    setIsDialogVisible(false);

    idToDelete.current = null;
  };

  const confirmDelete = () => {
    dispatch(deleteCommentAndUpdateGrid(idToDelete.current, dataState));
    closeDialog();
  };

  return (
    <div className={classes.container}>
      <Grid
        scrollable="none"
        data={comments}
        total={total}
        {...dataState}
        sortable={true}
        pageable={true}
        filterable
        filterOperators={{
          text: [{ text: "grid.filterContainsOperator", operator: "contains" }]
        }}
        onDataStateChange={handleDataStateChange}
      >
        {columns.map((field, index) => {
          const { name, title, cellType, filterable, sortable } = field;
          return (
            <Column
              key={index}
              field={name}
              title={title}
              sortable={sortable ?? true}
              cell={cellType}
              filterable={filterable}
            />
          );
        })}
        <Column
          field="_command"
          groupable={false}
          sortable={false}
          filterable={false}
          resizable={false}
          title="Edit / Delete"
          cell={props => (
            <CommandCell
              {...props}
              handleEdit={handleEditIconClick}
              handleDelete={handleDeleteIconClick}
            />
          )}
        />
      </Grid>

      <Button
        primary
        className={classes.insertButton}
        onClick={handleInsertClick}
      >
        Insert new
      </Button>

      {isDialogVisible && (
        <Dialog handleConfirm={confirmDelete} handleCancel={closeDialog} />
      )}
    </div>
  );
};

export default CommentsListContainer;
